/**
 * Customer data access for the agent portal.
 *
 * Reads and writes go through callables rather than straight to Firestore.
 * Two reasons, and both were load-bearing:
 *
 *  - The customer's `agentDataSharing` choice ('none' / 'assigned_only' /
 *    'any_agent') has to be honored on the read path. A Firestore list rule
 *    cannot filter on document content, so consent can only be applied
 *    server-side.
 *  - The old query pulled `userdata` documents with no `where` on the agent and
 *    filtered ownership in the browser, which meant every agent received every
 *    customer's full financial profile over the wire and any agent's book could
 *    be missing customers that happened to sort past the fetch window.
 *
 * The service surface is unchanged so page components did not have to move.
 *
 * As of the customer-insights move this file touches Firestore directly not at
 * all - there are no remaining reads that skip the consent gate.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { Customer, CustomerStatus, CustomerInsight } from '../types/agent';

/** Customer as it comes back over the wire - dates are ISO strings. */
interface WireCustomer {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	status: string;
	leadScore: number;
	assignedAgentId: string | null;
	tags: string[];
	notes: string;
	totalInteractions: number;
	createdAt: string | null;
	updatedAt: string | null;
	lastContactAt: string | null;
	lastActivityAt: string | null;
	userId: string;
	agentDataSharing: 'none' | 'assigned_only' | 'any_agent';
}

const toDate = (value: string | null): Date | undefined =>
	value ? new Date(value) : undefined;

const fromWire = (wire: WireCustomer): Customer => ({
	id: wire.id,
	firstName: wire.firstName,
	lastName: wire.lastName,
	email: wire.email,
	phone: wire.phone,
	status: wire.status as CustomerStatus,
	leadScore: wire.leadScore,
	assignedAgentId: wire.assignedAgentId,
	tags: wire.tags,
	notes: wire.notes,
	totalInteractions: wire.totalInteractions,
	createdAt: toDate(wire.createdAt) ?? new Date(),
	updatedAt: toDate(wire.updatedAt) ?? new Date(),
	lastContactAt: toDate(wire.lastContactAt),
	lastActivityAt: toDate(wire.lastActivityAt),
	userId: wire.userId,
	agentDataSharing: wire.agentDataSharing
} as Customer);

const listCustomers = httpsCallable<
	{
		assignedAgentId?: string;
		unassignedOnly?: boolean;
		status?: string;
		searchTerm?: string;
		sortBy?: string;
		sortOrder?: string;
		limit?: number;
		cursor?: string | null;
	},
	{ customers: WireCustomer[]; hasMore: boolean; nextCursor: string | null }
>(functions, 'listAgentCustomers');

const readCustomerCounts = httpsCallable<
	Record<string, never>,
	{ total: number; unassigned: number; byAgent: Record<string, number>; capped: boolean }
>(functions, 'getAgentCustomerCounts');

const readCustomer = httpsCallable<{ customerId: string }, WireCustomer>(
	functions,
	'getAgentCustomer'
);

const writeCustomer = httpsCallable<
	{
		customerId: string;
		updates: Record<string, unknown>;
		assignedAgentId?: string | null;
		incrementInteractions?: number;
	},
	{ success: boolean }
>(functions, 'updateAgentCustomer');

const removeCustomer = httpsCallable<{ customerId: string }, { success: boolean }>(
	functions,
	'deleteAgentCustomer'
);

const readCustomerInsights = httpsCallable<
	{ customerId: string; limit?: number },
	{ insights: Array<Record<string, any>> }
>(functions, 'getAgentCustomerInsights');

const addCustomer = httpsCallable<
	{
		firstName: string;
		lastName: string;
		email: string;
		phone?: string;
		status?: string;
		leadScore?: number;
		tags?: string[];
		notes?: string;
		assignedAgentId?: string;
	},
	{ success: boolean; customerId: string }
>(functions, 'createAgentCustomer');

export class CustomerService {
	/**
	 * Creates a hand-entered customer.
	 *
	 * This used to addDoc into the `customers` collection while every read path -
	 * the list, the detail page, the counts - reads `userdata`. An agent filled in
	 * the form, saw a success message, landed on the new customer's detail page,
	 * and found nothing there. The record was real; nothing could see it.
	 */
	static async createCustomer(customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
		if (!customer.email) {
			throw new Error('Customer email is required');
		}
		if (!customer.firstName || !customer.lastName) {
			throw new Error('Customer first and last name are required');
		}

		const result = await addCustomer({
			firstName: customer.firstName,
			lastName: customer.lastName,
			email: customer.email,
			phone: customer.phone,
			status: customer.status,
			leadScore: customer.leadScore,
			tags: customer.tags,
			notes: customer.notes,
			...(customer.assignedAgentId ? { assignedAgentId: customer.assignedAgentId } : {})
		});

		return result.data.customerId;
	}

	// Get a single customer by ID
	static async getCustomer(customerId: string): Promise<Customer | null> {
		try {
			const result = await readCustomer({ customerId });
			return fromWire(result.data);
		} catch (error: any) {
			// A customer who has withheld consent reads as absent, by design.
			if (error?.code === 'functions/not-found') {
				return null;
			}
			throw error;
		}
	}

	/**
	 * One page of customers.
	 *
	 * status, searchTerm, sorting and the unassigned filter are all applied
	 * server-side. Callers should page with `nextCursor` rather than asking for
	 * a huge `limit` and filtering in the browser - the latter both ships every
	 * record over the wire and silently truncates once the book outgrows the
	 * requested page.
	 */
	static async getCustomers(filters?: {
		assignedAgentId?: string;
		unassignedOnly?: boolean;
		status?: CustomerStatus;
		searchTerm?: string;
		sortBy?: 'createdAt' | 'leadScore' | 'lastActivityAt';
		sortOrder?: 'asc' | 'desc';
		limit?: number;
		cursor?: string | null;
	}): Promise<{ customers: Customer[]; hasMore: boolean; nextCursor: string | null }> {
		const result = await listCustomers({
			assignedAgentId: filters?.assignedAgentId,
			unassignedOnly: filters?.unassignedOnly,
			status: filters?.status,
			searchTerm: filters?.searchTerm,
			sortBy: filters?.sortBy || 'createdAt',
			sortOrder: filters?.sortOrder || 'desc',
			limit: filters?.limit || 25,
			cursor: filters?.cursor ?? null
		});

		return {
			customers: (result.data.customers || []).map(fromWire),
			hasMore: result.data.hasMore,
			nextCursor: result.data.nextCursor
		};
	}

	/**
	 * Customer counts by agent, plus unassigned and total, in a single call.
	 *
	 * Use this anywhere a screen needs a number rather than a list. The
	 * management and assignment screens previously fetched up to 1000 customer
	 * records per agent purely to read `.length`.
	 */
	static async getCustomerCounts(): Promise<{
		total: number;
		unassigned: number;
		byAgent: Record<string, number>;
		capped: boolean;
	}> {
		const result = await readCustomerCounts({});
		return result.data;
	}

	// Update customer
	static async updateCustomer(customerId: string, updates: Partial<Customer>): Promise<void> {
		const payload: Record<string, unknown> = {};

		if (updates.firstName !== undefined) payload.firstName = updates.firstName;
		if (updates.lastName !== undefined) payload.lastName = updates.lastName;
		if (updates.email !== undefined) payload.email = updates.email;
		if (updates.phone !== undefined) payload.phone = updates.phone;
		if (updates.status !== undefined) payload.status = updates.status;
		if (updates.leadScore !== undefined) payload.leadScore = updates.leadScore;
		if (updates.tags !== undefined) payload.tags = updates.tags;
		if (updates.notes !== undefined) payload.notes = updates.notes;
		if (updates.totalInteractions !== undefined) payload.totalInteractions = updates.totalInteractions;
		if (updates.lastInteractionType !== undefined) payload.lastInteractionType = updates.lastInteractionType;
		if (updates.lastContactAt !== undefined) {
			payload.lastContactAt = (updates.lastContactAt as Date)?.toISOString?.() ?? updates.lastContactAt;
		}
		if (updates.lastActivityAt !== undefined) {
			payload.lastActivityAt = (updates.lastActivityAt as Date)?.toISOString?.() ?? updates.lastActivityAt;
		}

		await writeCustomer({
			customerId,
			updates: payload,
			// Only send the assignment key when the caller actually meant to
			// change it - the server treats its presence as "reassign".
			...(updates.assignedAgentId !== undefined
				? { assignedAgentId: updates.assignedAgentId || null }
				: {})
		});
	}

	// Delete customer
	static async deleteCustomer(customerId: string): Promise<void> {
		await removeCustomer({ customerId });
	}

	// Assign customer to agent
	static async assignCustomerToAgent(customerId: string, agentId: string): Promise<void> {
		await writeCustomer({ customerId, updates: {}, assignedAgentId: agentId || null });
	}

	// Bulk assign customers to agent
	static async bulkAssignCustomers(customerIds: string[], agentId: string): Promise<void> {
		await Promise.all(
			customerIds.map(customerId => this.assignCustomerToAgent(customerId, agentId))
		);
	}

	/**
	 * Insights for one customer.
	 *
	 * This used to query `agentInsights` directly from the browser, keyed on the
	 * customer's userId, which meant it ignored their agentDataSharing choice
	 * entirely - a customer who had opted out still had their lead score,
	 * financial context and contact suggestions returned to any agent who opened
	 * their page. It was the last customer read outside the consent gate.
	 *
	 * Takes the customer document id, like every other customer callable; the
	 * server resolves the underlying user id itself.
	 */
	static async getCustomerInsights(customerId: string): Promise<CustomerInsight[]> {
		try {
			const result = await readCustomerInsights({ customerId });
			return (result.data.insights || []).map((insight) => ({
				...insight,
				createdAt: insight.createdAt ? new Date(insight.createdAt) : new Date(),
				updatedAt: insight.updatedAt ? new Date(insight.updatedAt) : new Date(),
				expiresAt: insight.expiresAt ? new Date(insight.expiresAt) : new Date()
			})) as CustomerInsight[];
		} catch (error: any) {
			// Withheld consent reads as absent, matching getCustomer.
			if (error?.code === 'functions/not-found') {
				return [];
			}
			throw error;
		}
	}

	// Update customer interaction
	static async recordInteraction(
		customerId: string,
		interactionType: 'call' | 'email' | 'meeting' | 'note'
	): Promise<void> {
		const now = new Date().toISOString();

		// The counter is incremented server-side rather than read-modify-written
		// here, so two agents logging a call at once don't both write the same
		// value.
		await writeCustomer({
			customerId,
			updates: {
				lastContactAt: now,
				lastActivityAt: now,
				lastInteractionType: interactionType
			},
			incrementInteractions: 1
		});
	}
}

// Keep the default export shape other modules may rely on
export const customerService = CustomerService;
